'use client';
import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '@whelk/store';
import { useSidebar } from '@whelk/components';
import usePageName from '@whelk/hooks/src/usePageName';
import { useHistoryListRoute } from '@whelk/hooks/src/useHistoryList';
import { HistoryListType } from '@whelk/store/slices/historyListSlice';
import { setIsShareSelecting } from '@whelk/store/slices/chatListSlice';
import { ExpandCollapseButtonIcon } from '@whelk/assets/src/web/home-icon';
import { checkLogin, openLoginModal, trackCustomEvent } from '@whelk/utils';

import HistoryItem from './item';
const MAX_HISTORY_COUNT = 5;

interface HistorySectionProps {
  icon: React.ReactElement;
  title: string;
  listType: HistoryListType;
  className?: string;
}

export interface HistorySectionInstance {
  unfold: () => void;
}

function HistorySection({ icon, title, listType, className }: HistorySectionProps) {
  const dispatch = useDispatch();
  const { getPageName } = usePageName();
  const { t } = useTranslation();

  const [isHistoryListUnfold, setIsHistoryListUnfold] = useState(true);

  const { goHistoryList } = useHistoryListRoute();
  const { handleMobileClose } = useSidebar();
  const currentChatID = useSelector((state: RootState) => state.chat.chatId);
  const isLogin = useSelector((state: RootState) => state.user.isLogin);
  const isShareSelecting = useSelector((state: RootState) => state.chat.isShareSelecting);
  const chatIds = useSelector((state: RootState) => state.history[listType]?.chatIds) ?? [];
  const renderList = chatIds.slice(0, MAX_HISTORY_COUNT);
  const showMoreEntrance = chatIds.length > MAX_HISTORY_COUNT;

  // 点击历史对话按钮
  const onClickHistoryBtn = () => {
    if (isShareSelecting) {
      dispatch(setIsShareSelecting(false));
    }
    handleMobileClose();
    if (!checkLogin()) {
      openLoginModal('chat_history_click');
      return;
    }
    trackCustomEvent({
      id: 'chat_history_click',
      page: getPageName(),
    });
    goHistoryList(listType);
  };

  //历史列表展开或者收起按钮
  const onClickMultiFunctionBtn = () => {
    if (!isLogin) {
      openLoginModal();
      return;
    }
    setIsHistoryListUnfold(!isHistoryListUnfold);
  };

  return (
    <div
      className={`
      grid transition-[grid-template-rows] duration-300 ease-in-out
      ${isHistoryListUnfold ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
      ${className || ''}
    `}
    >
      <div className='mb-[6px] flex min-h-[42px] flex-col overflow-hidden'>
        <nav
          className='md:hover:bg-col_bg13 mb-[2px] flex h-10 w-full flex-shrink-0 cursor-pointer items-center rounded-[12px]'
          onClick={onClickMultiFunctionBtn}
        >
          <div className='flex w-full min-w-[calc(var(--left-model-width)_-_56px)] items-center justify-between md:px-3'>
            <div className='flex gap-2'>
              {icon}
              <div className={`text-col_text00 text-[15px] font-[500]`}>{title}</div>
            </div>
            <div className={`transition duration-300 ${isHistoryListUnfold ? '' : 'rotate-180'}`}>
              <ExpandCollapseButtonIcon />
            </div>
          </div>
        </nav>
        <div className={`text-col_text01 pl-[18px] md:pl-[30px]`}>
          {!renderList.length ? (
            <div className='text-col_text03 flex h-[36px] items-center overflow-hidden pl-[10px] text-[14px]'>
              {t('text_no_chat_hist')}
            </div>
          ) : (
            <div className='flex flex-col'>
              {renderList.map((chatId) => (
                <HistoryItem
                  key={chatId}
                  chatId={chatId}
                  isCurrentChat={chatId === currentChatID}
                />
              ))}
              {showMoreEntrance && (
                <div
                  className='hover:bg-col_bg14 md:hover:text-col_text0 flex h-[36px] cursor-pointer items-center rounded-[12px] pl-[10px] text-[14px]'
                  onClick={onClickHistoryBtn}
                >
                  {t('button_view_all')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HistorySection;
